import React from 'react';
import { motion } from 'motion/react';
import { COND_TIERS } from './helpers';
import { findSkinImage } from './skinIndex';
import { ItemPicker, PRESETS, SOURCES, usePricerBasket } from './pricerShared';

// ─── Pricer ───────────────────────────────────────────────
// A quoting scratchpad. Pick any items, say how many of each, pick a market to
// read from, then move one percentage: the whole basket reprices at once and
// the footer says what the pile costs and what percentage of market that is.
//
// Nothing here touches the tracker's rows. The basket and its arithmetic live
// in pricerShared.js; this file is only the classic look over them.

const money = n => `$${(n ?? 0).toFixed(2)}`;

export default function PricerTab({
  skinsData = [], searchableOptions = [], livePrices, csfloatPrices,
  itemsWithConditions = new Set(),
}) {
  const b = usePricerBasket({ livePrices, csfloatPrices, itemsWithConditions });

  return (
    <div className="prc-root">

      {/* ── Item selector ──────────────────────────── */}
      <div className="prc-panel">
        <div className="prc-panel-head">
          <span className="prc-eyebrow">Basket</span>
          <span className="prc-sub">
            {b.lines.length
              ? `${b.lines.length} line${b.lines.length !== 1 ? 's' : ''} · ${b.units} item${b.units !== 1 ? 's' : ''}`
              : 'nothing picked yet'}
          </span>
          {b.lines.length > 0 && (
            <button className="prc-clear" onClick={b.clear}>Clear all</button>
          )}
        </div>

        <ItemPicker searchableOptions={searchableOptions} onPick={b.addItem}
          disabled={!searchableOptions.length} cls="prc" />

        {/* ── Global controls ──────────────────────── */}
        <div className="prc-controls">
          <div className="prc-control">
            <div className="prc-label">Percentage of market</div>
            <div className="prc-pct-row">
              <input type="range" min="0" max="150" step="0.5" className="prc-range"
                value={Math.min(150, Math.max(0, b.globalPct))}
                onChange={e => b.setPctText(e.target.value)} />
              <div className="prc-pct-box">
                <input value={b.pctText} onChange={e => b.setPctText(e.target.value)}
                  inputMode="decimal" aria-label="Percentage of market" />
                <span>%</span>
              </div>
            </div>
            <div className="prc-presets">
              {PRESETS.map(p => (
                <button key={p} className={`prc-chip${Math.abs(b.globalPct - p) < 0.001 ? ' on' : ''}`}
                  onClick={() => b.setPctText(String(p))}>{p}%</button>
              ))}
            </div>
          </div>

          <div className="prc-control">
            <div className="prc-label">Pricing source</div>
            <div className="prc-presets">
              {SOURCES.map(s => {
                const avail = s.id === 'buff' ? !!livePrices : !!csfloatPrices;
                return (
                  <button key={s.id} className={`prc-chip${b.source === s.id ? ' on' : ''}`}
                    disabled={!avail} onClick={() => b.setSource(s.id)}>
                    {s.label}{!avail ? ' (loading)' : ''}
                  </button>
                );
              })}
            </div>
            <div className="prc-sub" style={{ marginTop: 8 }}>
              {b.loaded
                ? `Every line reads its own wear tier from ${b.sourceLabel}.`
                : `Waiting on ${b.sourceLabel} prices…`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Lines ──────────────────────────────────── */}
      {b.lines.length === 0 ? (
        <div className="prc-empty">
          Search above to add items. Set a quantity on each, move the percentage bar,
          and the whole basket reprices as one.
        </div>
      ) : (
        <div className="prc-table">
          <div className="prc-row prc-head">
            <span>Item</span>
            <span>Condition</span>
            <span className="r">Qty</span>
            <span className="r">Unit</span>
            <span className="r">At market</span>
            <span className="r">%</span>
            <span className="r">Your price</span>
            <span />
          </div>

          {b.rows.map((r, i) => {
            const img      = findSkinImage(skinsData, r.name, r.condition);
            const tiers    = b.pricedTiers(r.name);
            const hasTiers = tiers.length > 0 || itemsWithConditions.has(r.name);
            return (
              <motion.div key={r.id} className="prc-row"
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 12) * 0.02 }}>

                <div className="prc-item">
                  {img ? <img src={img} alt="" /> : <div className="prc-noimg" />}
                  <span className="prc-name" title={r.name}>{r.name}</span>
                </div>

                <div>
                  {hasTiers ? (
                    <select className="prc-select" value={r.condition || ''}
                      onChange={e => b.patchLine(r.id, { condition: e.target.value || null })}>
                      <option value="">Any</option>
                      {COND_TIERS.map(t => {
                        const quoted = tiers.some(x => x.value === t.value);
                        return (
                          <option key={t.value} value={t.value}>
                            {t.value}{quoted ? '' : ' · n/a'}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <span className="prc-dim">n/a</span>
                  )}
                </div>

                <div className="r">
                  <div className="prc-qty">
                    <button onClick={() => b.patchLine(r.id, { qty: Math.max(1, r.qty - 1) })}
                      aria-label="One fewer">−</button>
                    <input value={r.qty} inputMode="numeric" aria-label="Quantity"
                      onChange={e => {
                        const n = parseInt(e.target.value.replace(/\D/g, ''), 10);
                        b.patchLine(r.id, { qty: isFinite(n) && n > 0 ? n : 1 });
                      }} />
                    <button onClick={() => b.patchLine(r.id, { qty: r.qty + 1 })}
                      aria-label="One more">+</button>
                  </div>
                </div>

                <div className="r prc-num">
                  {r.unit == null ? <span className="prc-dim">n/a</span> : money(r.unit)}
                </div>
                <div className="r prc-num">
                  {r.market == null ? <span className="prc-dim">n/a</span> : money(r.market)}
                </div>

                <div className="r">
                  {/* Blank means "follow the bar". A number here pins this one
                      line and takes it out of everything the bar moves. */}
                  <input className={`prc-line-pct${r.custom ? ' on' : ''}`}
                    value={r.pct} inputMode="decimal" aria-label={`Percentage for ${r.name}`}
                    placeholder={b.globalPct.toFixed(0)}
                    onChange={e => b.patchLine(r.id, { pct: e.target.value })} />
                </div>

                <div className="r prc-num prc-ask">
                  {r.ask == null ? <span className="prc-dim">n/a</span> : money(r.ask)}
                </div>

                <div className="r">
                  <button className="prc-x" onClick={() => b.removeLine(r.id)} aria-label="Remove line">×</button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Footer total ───────────────────────────── */}
      {b.lines.length > 0 && (
        <div className="prc-total">
          <div className="prc-total-cell">
            <div className="k">At market</div>
            <div className="v">{money(b.marketTotal)}</div>
            <div className="s">{b.units} item{b.units !== 1 ? 's' : ''} · {b.sourceLabel} at 100%</div>
          </div>

          <div className="prc-total-cell">
            <div className="k">Your price</div>
            <div className="v big">
              {/* Editable: type what the pile has to come to and the bar moves
                  to whatever percentage that turns out to be. */}
              <span className="cur">$</span>
              <input className="prc-total-input"
                value={b.targetDraft ?? b.askTotal.toFixed(2)}
                inputMode="decimal" aria-label="Target total"
                onChange={e => b.applyTarget(e.target.value)}
                onBlur={() => b.setTargetDraft(null)}
                onKeyDown={e => { if (e.key === 'Enter') { b.setTargetDraft(null); e.currentTarget.blur(); } }} />
            </div>
            <div className="s">
              {b.effectivePct != null
                ? `${money(b.askTotal)} at ${b.effectivePct.toFixed(1)}% of market`
                : 'no prices to work from yet'}
            </div>
          </div>

          <div className="prc-total-cell">
            <div className="k">Difference</div>
            {/* The gap itself, unsigned — "under market" below it is what says
                which way it runs, so a minus sign in front would say it twice. */}
            <div className="v" style={{ color: b.askTotal <= b.marketTotal ? 'var(--green-dim)' : 'var(--red)' }}>
              {money(Math.abs(b.marketTotal - b.askTotal))}
            </div>
            <div className="s">
              {b.askTotal <= b.marketTotal ? 'under' : 'over'} market
              {b.unpriced > 0 && ` · ${b.unpriced} line${b.unpriced !== 1 ? 's' : ''} unpriced`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
